import { HttpResponse } from "@angular/common/http";
import { Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { IExposedAddress, ILab, IModifyAddress, INewAddress } from "@gloria-public/order/domain/interfaces/address.interface";
import { ICartItem, ShippingType } from "@gloria-public/order/domain/interfaces/cart.interface";
import { IModifyShippingDetails } from "@gloria-public/order/domain/interfaces/order.interface";
import { AddressService } from "@gloria-public/order/domain/services/address.service";
import { OrderService } from "@gloria-public/order/domain/services/order.service";
import { TranslateService } from "@ngx-translate/core";
import { IMapMarker, MapComponent } from "@shared/components/map/map.component";
import { CountryService } from "@shared/services/country.service";
import {
  Observable,
  Subject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  finalize,
  first,
  map,
  of,
  startWith,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from "rxjs";

@Component({
  selector: "app-checkout-page",
  templateUrl: "./checkout-page.component.html",
  styleUrls: ["./checkout-page.component.scss"],
})
export class CheckoutPageComponent implements OnInit, OnDestroy {
  @ViewChild(MapComponent) mapComponent!: MapComponent;
  @ViewChild("addressSection") private addressSection?: ElementRef<HTMLDivElement>;
  @ViewChild("billingSection") private billingSection?: ElementRef<HTMLDivElement>;
  @ViewChild("labSection") private labSection?: ElementRef<HTMLDivElement>;
  @ViewChild("labGrid") private labGrid?: ElementRef<HTMLDivElement>;
  @ViewChildren("labCard") private labCards?: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChild("addAddress") private addAddress?: ElementRef<HTMLDivElement>;

  public countries: { code: string; name: string }[] = [];
  public addresses: IExposedAddress[] = [];
  public labs: ILab[] = [];
  public selectedAddress: IExposedAddress | null = null;
  public selectedLab: ILab | null = null;
  public selectedBillingAddress: IExposedAddress | null = null;
  public isLoadingAddresses = false;
  public addressFormVisible = false;
  public editingAddressId: number | null = null;
  public addressFormSource: "shipping" | "billing" | null = null;

  public get isEditing(): boolean {
    return this.editingAddressId !== null;
  }

  public addressForm = this.fb.nonNullable.group({
    street: ["", Validators.required],
    number: ["", Validators.required],
    location: ["", Validators.required],
    postalCode: ["", Validators.required],
    country: ["", Validators.required],
    setAsHome: [false],
  });
  public ShippingType = ShippingType;
  public postalCodeSearch = "";
  public showLabList = false;
  public searchedLab = false;
  public mapDefaultLat = 50.6411; // Default to Belgium center (or your country)
  public mapDefaultLng = 4.6689;
  public realCartItems: ICartItem[] = [];
  public dueToday$ = this.orderService.dueToday$;

  /** Emits when the component is destroyed, used to complete all active subscriptions */
  private onDestroy$ = new Subject<void>();

  public shippingSummary: {
    total: number;
    homeCount: number;
    labCount: number;
    missingShippingMethod: number;
  } = { total: 0, homeCount: 0, labCount: 0, missingShippingMethod: 0 };
  public canPlaceOrder = false;
  public selectingHomeAddress = true;
  public selectingRelayPoint = true;
  public selectingBillingAddress = true;
  public useSameAsHomeForBilling = false;
  public highlightedSection: "addressForm" | "address" | "billing" | "lab" | null = null;

  constructor(
    private orderService: OrderService,
    private addressService: AddressService,
    private countryService: CountryService,
    private translateService: TranslateService,
    private router: Router,
    private fb: FormBuilder,
    public snackBar: MatSnackBar
  ) {}

  /**
   * Initializes the checkout page data and subscriptions.
   */
  ngOnInit(): void {
    combineLatest([this.loadCartItems$(), this.loadUserAddresses$()])
      .pipe(
        first(),
        switchMap(([items, addresses]) => {
          const userAddressItem = items.find((i) => i.shippingType === ShippingType.USER_ADDRESS);

          this.selectedAddress = userAddressItem ? addresses.find((a) => a.id === Number(userAddressItem.shippingAddress)) ?? null : null;
          if (this.selectedAddress) {
            this.selectingHomeAddress = false;
          }

          const relayPointItem = items.find((i) => i.shippingType === ShippingType.RELAY_POINT);

          if (!relayPointItem?.shippingAddress) {
            return of(null);
          }

          return this.addressService.getLabs(undefined, relayPointItem.shippingAddress).pipe(
            tap((labs) => {
              this.selectedLab = labs[0] ?? null;

              if (this.selectedLab) {
                this.selectingRelayPoint = false;
              }
            })
          );
        })
      )
      .subscribe(() => {
        this.updateCanPlaceOrder();
      });
    this.orderService.realCartItems$.pipe(takeUntil(this.onDestroy$)).subscribe((items) => {
      this.realCartItems = items;
      this.shippingSummary = {
        total: this.realCartItems.length,
        homeCount: this.realCartItems.filter((i) => i.shippingType === ShippingType.USER_ADDRESS).length,
        labCount: this.realCartItems.filter((i) => i.shippingType === ShippingType.RELAY_POINT).length,
        missingShippingMethod: this.realCartItems.filter((i) => !i.shippingType && i.availableShippingTypes.length !== 0).length,
      };
      if (!this.hasHomeShipping) {
        this.useSameAsHomeForBilling = false;
        if (!this.selectedBillingAddress) {
          this.selectingBillingAddress = true;
        }
      }
      this.updateCanPlaceOrder();
    });
    this.translateService.onLangChange
      .pipe(
        startWith({
          lang: this.translateService.currentLang,
        }),
        distinctUntilChanged((a, b) => a.lang === b.lang),
        takeUntil(this.onDestroy$)
      )
      .subscribe((event) => {
        this.countries = this.countryService.getCountries(event.lang);

        if (this.countries.length > 0 && !this.addressForm.get("country")?.value) {
          this.addressForm.patchValue({
            country: this.countries[0].code,
          });
        }
      });
  }

  /**
   * Cleans up subscriptions and subjects when the component is destroyed.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Loads the latest cart items.
   */
  private loadCartItems(): void {
    this.loadCartItems$().subscribe();
  }

  /**
   * Loads the latest cart items as an observable.
   * @returns Observable emitting the loaded cart items.
   */
  private loadCartItems$(): Observable<ICartItem[]> {
    return this.orderService.getCartItems(this.translateService.currentLang).pipe(
      first(),
      tap((items) => {
        if (!items.length) {
          this.router.navigate(["/cart"]);
          return;
        }
      }),
      catchError((error) => {
        this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
          duration: 5000,
        });

        this.router.navigate(["/cart"]);

        return throwError(() => error);
      }),
      finalize(() => this.updateCanPlaceOrder())
    );
  }

  /**
   * Selects the shipping method for a cart item.
   * @param item Cart item to update.
   * @param type Shipping method to apply.
   */
  public selectItemShipping(item: ICartItem, type: ShippingType): void {
    if (item.shippingType === type) {
      if (type === ShippingType.USER_ADDRESS && !this.selectedAddress) {
        this.addressSection?.nativeElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        this.highlight("address");
      } else if (type === ShippingType.RELAY_POINT && !this.selectedLab) {
        this.labSection?.nativeElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        this.highlight("lab");
      }
      return;
    }

    let shippingAddress: string | null;

    switch (type) {
      case ShippingType.USER_ADDRESS:
        shippingAddress = this.selectedAddress?.id.toString() ?? null;
        break;
      case ShippingType.RELAY_POINT:
        shippingAddress = this.selectedLab?.identifier.value ?? null;
        break;
    }

    this.orderService.patchRealCartItem(item.id, {
      shippingType: type,
      shippingAddress,
    });

    this.updateShippingDetails$().subscribe({
      next: () => {
        if (type === ShippingType.USER_ADDRESS && !this.selectedAddress) {
          // Update scroll after a slight delay to ensure section loaded
          setTimeout(() => {
            this.addressSection?.nativeElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
            this.highlight("address");
          }, 100);
        } else if (type === ShippingType.RELAY_POINT && !this.selectedLab) {
          // Update scroll after a slight delay to ensure section loaded
          setTimeout(() => {
            this.labSection?.nativeElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
            this.highlight("lab");
          }, 100);
        }
      },
    });
  }

  /**
   * Loads the user's addresses.
   */
  private loadUserAddresses(): void {
    this.loadUserAddresses$().subscribe((items) => {
      this.selectedAddress = items.find((item) => item.id === this.selectedAddress?.id) ?? null;
      this.selectedBillingAddress = items.find((item) => item.id === this.selectedBillingAddress?.id) ?? null;
    });
  }

  /**
   * Loads the user's addresses as an observable.
   * @returns Observable emitting the user's addresses.
   */
  private loadUserAddresses$(): Observable<IExposedAddress[]> {
    this.isLoadingAddresses = true;
    return this.addressService.getUserAddresses().pipe(
      first(),
      tap((addresses) => {
        this.addresses = addresses;
        this.isLoadingAddresses = false;
      }),
      catchError((error) => {
        this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
        this.isLoadingAddresses = false;

        this.router.navigate(["/cart"]);

        return throwError(() => error);
      })
    );
  }

  /**
   * Opens shipping address selection mode.
   */
  public editHomeAddressSelection(): void {
    this.selectingHomeAddress = true;
  }

  /**
   * Cancels shipping address selection mode.
   */
  public cancelHomeAddressSelection(): void {
    this.selectingHomeAddress = false;

    if (this.addressFormSource === "shipping") {
      this.editingAddressId = null;
      this.addressFormVisible = false;
    }
  }

  /**
   * Returns the currently selected billing address identifier.
   * @returns Selected billing address identifier, or null if none is selected.
   */
  public get billingAddressId(): number | null {
    return this.hasHomeShipping && this.useSameAsHomeForBilling
      ? this.selectedAddress?.id ?? null
      : this.selectedBillingAddress?.id ?? null;
  }

  /**
   * Opens billing address selection mode.
   */
  public editBillingAddressSelection(): void {
    this.selectingBillingAddress = true;

    if (this.addressFormSource === "billing") {
      this.editingAddressId = null;
      this.addressFormVisible = false;
    }
  }

  /**
   * Cancels billing address selection mode.
   */
  public cancelBillingAddressSelection(): void {
    this.selectingBillingAddress = false;
  }

  /**
   * Opens relay point selection mode.
   */
  public editRelayPointSelection(): void {
    this.selectingRelayPoint = true;

    this.postalCodeSearch = "";
    this.searchedLab = false;
    this.labs = [this.selectedLab!];
    this.showLabList = true;

    // Update markers after a slight delay to ensure map is initialized
    setTimeout(() => {
      this.updateMapMarkers();
    }, 100);
  }

  /**
   * Cancels relay point selection mode.
   */
  public cancelRelayPointSelection(): void {
    this.selectingRelayPoint = false;
  }

  /**
   * Loads labs using the postal code
   */
  private loadLabs(): void {
    this.addressService
      .getLabs(this.postalCodeSearch)
      .pipe(first())
      .subscribe({
        next: (labs) => {
          this.labs = labs;
          // Filter labs by valid coordinates
          this.labs = this.labs.filter((lab) => {
            return this.isValidCoordinate(lab.latitude) && this.isValidCoordinate(lab.longitude);
          });
          this.showLabList = true;
          this.searchedLab = true;

          // Update markers after a slight delay to ensure map is initialized
          setTimeout(() => {
            this.updateMapMarkers();
          }, 100);
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", {
            duration: 5000,
          });
          this.router.navigate(["/cart"]);
        },
      });
  }

  /**
   * Searches relay points by postal code.
   */
  public searchLabsByPostalCode(): void {
    if (!this.postalCodeSearch.trim()) {
      this.snackBar.open(this.translateService.instant("page.order.checkout.shippingType.relay.enterPostalCode"), "OK", { duration: 3000 });
      return;
    }

    this.loadLabs();
  }

  /**
   * Validates coordinate value
   * @param coord Coordinate value to validate
   * @return True if valid coordinate
   */
  private isValidCoordinate(coord: string | number | null | undefined): boolean {
    if (coord === null || coord === undefined || coord === "") {
      return false;
    }
    const num = typeof coord === "string" ? parseFloat(coord) : coord;
    return !isNaN(num) && isFinite(num);
  }

  /**
   * Updates the markers displayed on the map.
   */
  private updateMapMarkers(): void {
    if (this.mapComponent) {
      const markers: IMapMarker[] = this.labs.map((lab) => ({
        id: lab.identifier.value,
        lat: parseFloat(lab.latitude),
        lng: parseFloat(lab.longitude),
        title: lab.name,
        popup: `<strong>${lab.name}</strong><br>${lab.street} ${lab.number}<br>${lab.postalCode} ${lab.city}`,
      }));
      this.mapComponent.clearMarkers();
      this.mapComponent.addMarkers(markers);
      this.mapComponent.centerMarkers();

      if (this.selectedLab) {
        const selectedLab = this.selectedLab;
        const marker = markers.find(({ id }) => id === selectedLab.identifier.value);

        if (marker) {
          this.mapComponent.openMarkerPopup(marker.id);
        }
      }
    }
  }

  /**
   * Clears the current relay point search.
   */
  public clearLabSearch(): void {
    this.postalCodeSearch = "";
    this.searchedLab = false;
    this.labs = this.selectedLab ? [this.selectedLab] : [];
    if (this.labs.length === 0) {
      this.showLabList = false;
      if (this.mapComponent) {
        this.mapComponent.clearMarkers();
        this.mapComponent.centerMap(this.mapDefaultLat, this.mapDefaultLng);
      }
    } else {
      // Update markers after a slight delay to ensure map is initialized
      setTimeout(() => {
        this.updateMapMarkers();
      }, 100);
    }
  }

  /**
   * Selects the shipping address.
   * @param address Address to use for home delivery.
   */
  public selectAddress(address: IExposedAddress): void {
    this.selectedAddress = address;

    const shippingAddress = address.id.toString();

    this.orderService.patchRealCartItems(
      (item) => item.shippingType === ShippingType.USER_ADDRESS && item.shippingAddress !== shippingAddress,
      { shippingAddress: shippingAddress }
    );

    this.updateShippingDetails();
  }

  /**
   * Handles marker click from map
   * @param id Marker identifier
   */
  public onMarkerIdClicked(id: string): void {
    const lab = this.labs.find((l) => l.identifier.value === id);

    if (lab) {
      this.selectLab(lab);
    }
  }

  /**
   * Selects a relay point.
   * @param lab Relay point to select.
   * @param popup Whether to open the marker popup.
   */
  public selectLab(lab: ILab, popup = false): void {
    this.selectedLab = lab;

    const shippingAddress = lab.identifier.value;

    this.orderService.patchRealCartItems(
      (item) => item.shippingType === ShippingType.RELAY_POINT && item.shippingAddress !== shippingAddress,
      { shippingAddress: shippingAddress }
    );

    this.updateShippingDetails();

    queueMicrotask(() => this.scrollToSelectedLab());

    if (popup && this.mapComponent) {
      this.mapComponent.openMarkerPopup(shippingAddress);
    }
  }

  /**
   * Scrolls the selected relay point into view.
   */
  private scrollToSelectedLab(): void {
    if (!this.labGrid || !this.labCards) {
      return;
    }

    const index = this.labs.findIndex((l) => l.identifier.value === this.selectedLab?.identifier.value);

    if (index === -1) {
      return;
    }

    const container = this.labGrid.nativeElement;
    const card = this.labCards.get(index)?.nativeElement;

    if (!card) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();

    const delta = cardRect.top + cardRect.height / 2 - (containerRect.top + containerRect.height / 2);

    container.scrollBy({
      top: delta,
      behavior: "smooth",
    });
  }

  /**
   * Opens add address form
   *
   */
  public openAddAddressForm(addressFormSource: "shipping" | "billing" | null): void {
    this.editingAddressId = null;

    this.addressForm.reset({ country: this.countries[0]?.code ?? "", setAsHome: false });
    this.addressFormVisible = true;
    this.addressFormSource = addressFormSource;

    // Update scroll after a slight delay to ensure section loaded
    setTimeout(() => {
      this.addAddress?.nativeElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      this.highlight("addressForm");
    }, 100);
  }

  /**
   * Opens the address form for editing.
   * @param address Address to edit.
   * @param addressFormSource Indicates whether the form was opened from the shipping or billing section.
   */
  public openEditAddressForm(address: IExposedAddress, addressFormSource: "shipping" | "billing" | null): void {
    this.addressFormSource = addressFormSource;
    this.editingAddressId = address.id;

    this.addressForm.patchValue({
      street: address.street,
      number: address.number,
      location: address.location,
      postalCode: address.postalCode,
      country: address.country,
      setAsHome: address.isHome,
    });

    const home = this.addressForm.get("setAsHome");
    address.isHome ? home?.disable() : home?.enable();

    this.addressFormVisible = true;

    // Update scroll after a slight delay to ensure section loaded
    setTimeout(() => {
      this.addAddress?.nativeElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      this.highlight("addressForm");
    }, 100);
  }

  /**
   * Closes and resets the address form.
   */
  public closeAddressForm(): void {
    this.addressFormVisible = false;
    if (this.addressFormSource === "shipping") {
      // Update scroll after a slight delay to ensure section loaded
      setTimeout(() => {
        this.addressSection?.nativeElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        this.highlight("address");
      }, 100);
    } else if (this.addressFormSource === "billing") {
      // Update scroll after a slight delay to ensure section loaded
      setTimeout(() => {
        this.billingSection?.nativeElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        this.highlight("billing");
      }, 100);
    }
    this.addressFormSource = null;
    this.editingAddressId = null;
    this.addressForm.get("setAsHome")?.enable();
    this.addressForm.reset();
    this.addressForm.reset({
      country: this.countries[0]?.code ?? "",
      setAsHome: false,
    });
  }

  /**
   * Saves the address currently entered in the form.
   */
  public saveAddress(): void {
    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      this.snackBar.open(this.translateService.instant("page.order.checkout.newAddress.requiredFields"), "OK", { duration: 3000 });
      return;
    }

    if (this.isEditing) {
      this.saveEditedAddress();
    } else {
      this.addNewAddress();
    }
  }

  /**
   * Creates a new address.
   */
  private addNewAddress(): void {
    const addressData: INewAddress = this.addressForm.getRawValue();
    this.addressForm.disable();

    this.addressService
      .addAddress(addressData)
      .pipe(
        first(),
        finalize(() => this.addressForm.enable())
      )
      .subscribe({
        next: (address: IExposedAddress) => {
          if (address.isHome) {
            this.addresses.forEach((a) => (a.isHome = false));
          }

          this.addresses.push(address);
          if (this.addressFormSource == "shipping") {
            this.selectAddress(address);
          } else if (this.addressFormSource == "billing") {
            this.selectedBillingAddress = address;
            this.updateShippingDetails();
          }

          this.closeAddressForm();

          this.snackBar.open(`✅ ${this.translateService.instant("page.order.checkout.newAddress.successAdd")}`, "OK", { duration: 3000 });
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
        },
      });
  }

  /**
   * Saves edited address
   */
  private saveEditedAddress(): void {
    if (this.editingAddressId === null) return;
    this.addressForm.disable();

    // Use RawValue so that the disables setAsHome is still included
    const formValue = this.addressForm.getRawValue();
    const editData: IModifyAddress = {
      street: formValue.street,
      number: formValue.number,
      location: formValue.location,
      postalCode: formValue.postalCode,
      country: formValue.country,
      setAsHome: formValue.setAsHome,
    };

    this.addressService
      .modifyAddress(this.editingAddressId, editData)
      .pipe(
        first(),
        finalize(() => this.addressForm.enable())
      )
      .subscribe({
        next: (response: HttpResponse<number>) => {
          if (response.status === 200) {
            this.updateEditedAddress(editData);
          } else if (response.status === 201) {
            if (response.body) {
              this.replaceAddress(response.body, editData);
            }
          }

          this.closeAddressForm();
          this.snackBar.open(`✅ ${this.translateService.instant("page.order.checkout.newAddress.successUpdate")}`, "OK", {
            duration: 3000,
          });
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
        },
      });

    this.addressService
      .modifyAddress(this.editingAddressId, editData)
      .pipe(
        first(),
        finalize(() => this.addressForm.enable())
      )
      .subscribe({
        next: () => {
          this.loadUserAddresses();
          this.closeAddressForm();
          this.snackBar.open(`✅ ${this.translateService.instant("page.order.checkout.newAddress.successUpdate")}`, "OK", {
            duration: 3000,
          });
        },
        error: () => {
          this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
        },
      });
  }

  /**
   * Updates the edited address in the local address list.
   * Also updates the selected shipping and billing addresses if they reference the same object.
   */
  private updateEditedAddress(editData: IModifyAddress): void {
    const address = this.addresses.find((a) => a.id === this.editingAddressId);

    if (!address) {
      return;
    }

    if (editData.street != null) {
      address.street = editData.street;
    }

    if (editData.number != null) {
      address.number = editData.number;
    }

    if (editData.location != null) {
      address.location = editData.location;
    }

    if (editData.postalCode != null) {
      address.postalCode = editData.postalCode;
    }

    if (editData.country != null) {
      address.country = editData.country;
    }

    if (editData.setAsHome != null) {
      address.isHome = editData.setAsHome;
    }
  }

  /**
   * Replaces references to the edited address in cart items after a new address is created.
   * Updates the local address beforehand.
   */
  private replaceAddress(newAddressId: number, editData: IModifyAddress): void {
    this.updateEditedAddress(editData);

    this.orderService.patchRealCartItems(
      (item) => item.shippingType === ShippingType.USER_ADDRESS && item.shippingAddress === this.editingAddressId?.toString(),
      { shippingAddress: newAddressId.toString() },
      false
    );
  }

  /**
   * Selects the billing address.
   * @param address Address to use for billing.
   */
  public selectBillingAddress(address: IExposedAddress): void {
    if (address.id !== this.selectedAddress?.id) {
      this.useSameAsHomeForBilling = false;
    }
    this.selectedBillingAddress = address;
    this.updateShippingDetails();
    this.updateCanPlaceOrder();
  }

  public get hasHomeShipping(): boolean {
    return this.shippingSummary.homeCount > 0;
  }

  /**
   * Toggles whether the billing address matches the shipping address.
   * @param checked Whether the billing address should match the shipping address.
   */
  public toggleSameAsHome(checked: boolean): void {
    if (!this.hasHomeShipping) {
      return;
    }

    this.useSameAsHomeForBilling = checked;

    if (checked) {
      this.selectedBillingAddress = null; // fall back to selectedAddress via getter
      this.updateShippingDetails();
    }
    this.updateCanPlaceOrder();
  }

  /**
   * Updates the shipping details.
   */
  private updateShippingDetails(): void {
    this.updateShippingDetails$().subscribe();
  }

  /**
   * Updates the shipping details as an observable.
   * @returns Observable completing when the shipping details have been updated.
   */
  private updateShippingDetails$(): Observable<void> {
    const modifiedItems = this.realCartItems.filter(
      (
        item
      ): item is ICartItem & {
        shippingType: ShippingType;
        shippingAddress: string;
      } => this.orderService.isModified(item.id) && item.shippingType != null && item.shippingAddress != null
    );
    if (!modifiedItems.length && !this.billingAddressId) {
      return of(void 0);
    }

    const shippingDetails: IModifyShippingDetails = {
      billingAddressID: this.billingAddressId ?? null,
      shippingInformation: modifiedItems.map((item) => ({
        itemID: item.id,
        shippingType: item.shippingType,
        shippingAddressID: item.shippingAddress,
      })),
    };
    return this.orderService.modifyOrder(shippingDetails).pipe(
      first(),
      tap(() => {
        this.orderService.clearModified(modifiedItems.map((item) => item.id));
        this.snackBar.open(`✅ ${this.translateService.instant("page.order.checkout.successUpdate")}`, "OK", { duration: 5000 });
      }),
      map(() => void 0),
      catchError((err) => {
        this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
        if (err.status === 400 || err.status === 409) {
          this.loadCartItems();
          this.updateCanPlaceOrder();
        } else if (err.status === 404) {
          this.router.navigate(["/cart"]);
        }

        return throwError(() => err);
      })
    );
  }

  // TODO GLOR-253: Revoir comment gerer le changement de prix d'un article ayant été ajouté au panier
  // Possible régulation européenne obligeant l'utilisateur a validé qu'il a vu ce changement de prix
  /**
   * Places order
   */
  public placeOrder(): void {
    if (!this.canPlaceOrder) {
      this.snackBar.open(this.translateService.instant("page.order.checkout.noneSelected"), "OK", {
        duration: 3000,
      });
      return;
    }

    this.router.navigate(["/payment"]);
  }

  /**
   * Updates canPlaceOrder variable
   */
  private updateCanPlaceOrder(): void {
    this.canPlaceOrder = this.realCartItems.every((item) => {
      if (item.availableShippingTypes.length > 0 && !item.shippingType) return false;
      if (item.shippingType === ShippingType.USER_ADDRESS && !this.selectedAddress) return false;
      if (item.shippingType === ShippingType.RELAY_POINT && !this.selectedLab) return false;
      if (this.billingAddressId === null) return false;
      return true;
    });
  }

  private highlight(section: "addressForm" | "address" | "billing" | "lab"): void {
    this.highlightedSection = section;

    setTimeout(() => {
      // Only clear if we're still highlighting the same section
      if (this.highlightedSection === section) {
        this.highlightedSection = null;
      }
    }, 1500);
  }
}
